<script>
import { Plugins } from '@capacitor/core'

const { Device } = Plugins

import takePicture from '@/utils/takePicture'

const MAX_FILE_SIZE = 8 * 1000000 // 8 MB

export default {
  name: 'UploadImage',
  props: {
    defaultSrc: { type: String, default: '' },
    rounded: { type: Boolean, default: false },
    size: { type: Number, default: 50 }
  },
  data() {
    return {
      id: null,

      src: '',
      loading: false,
      platform: null
    }
  },
  created() {
    this.id = this._uid

    this.src = this.defaultSrc

    // Get device platform (web or mobile)
    Device.getInfo().then(({ platform }) => {
      this.platform = platform
    })
  },
  methods: {
    onClick() {
      if (this.loading) return
      this.loading = true

      takePicture()
        .then(this.submit)
        .catch(error => {
          if (error !== 'User cancelled photos app')
            this.$toast.error(error.toString())
        })
        .finally(() => (this.loading = false))
    },

    onFileChange(e) {
      const [file] = e.target.files
      if (!file) return

      const src = URL.createObjectURL(file)

      this.submit({ src, file })
    },

    submit({ src, file }) {
      if (file.size > MAX_FILE_SIZE) {
        return this.$toast.error('Uploaded file is too large')
      }
      this.src = src

      this.$emit('input', { src, file })
    }
  }
}
</script>

<template>
  <div>
    <!-- Web input -->
    <div v-if="platform === 'web'" class="d-flex">
      <label
        :for="id"
        :class="{ 'file-input-label': true, rounded }"
        :style="{ height: size + 'px', width: size + 'px' }"
      >
        <v-img
          v-if="src"
          :src="src"
          :lazy-src="src"
          :height="size"
          :width="size"
          eager
        />

        <v-icon v-else color="black" v-text="'$vuetify.icons.camera'" />
      </label>
      <input
        :id="id"
        type="file"
        name="photo"
        class="file-input"
        @change="onFileChange"
      />
    </div>

    <!-- Mobile only input -->
    <div
      v-else
      :class="{ 'camera-input': true, rounded }"
      :style="{ height: size + 'px', width: size + 'px' }"
      @click="onClick"
    >
      <v-row
        v-if="loading"
        class="fill-height ma-0 grey lighten-2"
        align="center"
        justify="center"
      >
        <v-progress-circular
          indeterminate
          color="grey lighten-5"
        ></v-progress-circular>
      </v-row>

      <v-img
        v-else-if="src"
        :src="src"
        :lazy-src="src"
        aspect-ratio="1"
        :height="size"
        :width="size"
        eager
      />

      <v-icon v-else color="black" v-text="'$vuetify.icons.camera'" />
    </div>
  </div>
</template>

<style scoped lang="sass">
.camera-input, .file-input-label
  display: flex
  justify-content: center
  overflow: hidden
  border-radius: 3px
  border: 1px solid #bbb
  img
    width: 100%
    height: 100%
  &.rounded
    border-radius: 50%

.file-input
  width: 0.1px
  height: 0.1px
  opacity: 0
  overflow: hidden
  position: absolute
  z-index: -1
  &::-webkit-file-upload-button
    visibility: hidden
</style>
